/**
 * DomainAliasesTab — 收件域右侧"别名与转发"Tab
 * 展示选中收件域下的别名：新建/删除/多选/分页/详情（新建自动带入选中域名，实时生效）。
 */
import { useMemo, useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  Text,
  Input,
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  InfoLabel,
  Switch,
} from '@fluentui/react-components'
import { AddRegular } from '@fluentui/react-icons'
import { aliasesApi } from '@/api/aliases'
import type { Alias, Domain } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { PagedGrid, type PagedColumn } from '@/components/PagedGrid'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import { DetailGrid, DetailField } from '@/components/DetailField'

const PAGE_SIZE = 10

interface AliasPayload {
  domain_id: number
  address: string
  target: string
}

interface AliasFormState {
  address: string
  target: string
}

const EMPTY_FORM: AliasFormState = { address: '', target: '' }

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px 16px' },
  formGridItem: { minWidth: 0 },
  dialogSurface: { width: '65vw', minWidth: '600px', maxWidth: '1000px' },
})

export default function DomainAliasesTab({ domain }: { domain: Domain }) {
  const styles = useStyles()
  const queryClient = useQueryClient()

  const { data: aliases, isLoading } = useQuery({
    queryKey: ['aliases'],
    queryFn: () => aliasesApi.list(),
  })

  const domainAliases = useMemo(
    () => (aliases ?? []).filter((a) => a.domain === domain.name),
    [aliases, domain.name],
  )

  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  const filteredAliases = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return domainAliases
    return domainAliases.filter(
      (a) => a.address.toLowerCase().includes(q) || a.target.toLowerCase().includes(q),
    )
  }, [domainAliases, query])

  const totalPages = Math.max(1, Math.ceil(filteredAliases.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageItems = filteredAliases.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  useEffect(() => {
    setQuery('')
    setPage(1)
  }, [domain.id])

  // 新建
  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState<AliasFormState>(EMPTY_FORM)
  const openCreate = () => {
    setForm(EMPTY_FORM)
    setCreateOpen(true)
  }

  const createMutation = useMutation({
    mutationFn: async (data: AliasPayload) => aliasesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aliases'] })
      toast.success('创建成功', '别名已创建，实时生效')
      setCreateOpen(false)
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : '创建失败，请稍后重试'
      toast.error('创建失败', msg)
    },
  })

  const handleSubmit = () => {
    const address = form.address.trim()
    const target = form.target.trim()
    if (!address) {
      toast.error('请填写地址')
      return
    }
    if (!target) {
      toast.error('请填写目标地址')
      return
    }
    createMutation.mutate({ domain_id: domain.id, address, target })
  }

  // 多选 + 批量删除
  const [selectionMode, setSelectionMode] = useState<'single' | 'multiselect'>('single')
  const [selectedRows, setSelectedRows] = useState<Alias[]>([])

  const deleteMutation = useMutation({
    mutationFn: async (rows: Alias[]) => {
      await Promise.all(rows.map((a) => aliasesApi.delete(a.id)))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aliases'] })
      toast.success('删除成功', `已删除 ${selectedRows.length} 项，实时生效`)
    },
    onError: (err) => toast.error('删除失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  // 详情
  const [detailAlias, setDetailAlias] = useState<Alias | null>(null)

  const columns: PagedColumn<Alias>[] = [
    {
      key: 'address',
      header: '地址',
      headerTooltip: '别名邮箱的完整地址，收件人写此地址时邮件会被接管转发',
      width: '220px',
      render: (a) => <Text weight="semibold">{a.address}@{a.domain}</Text>,
    },
    {
      key: 'target',
      header: '目标地址',
      headerTooltip: '别名转发到的目标邮箱地址',
      width: '260px',
      render: (a) => <span title={a.target}>{a.target}</span>,
    },
  ]

  return (
    <div className={styles.root}>
      <PagedGrid<Alias>
        rows={pageItems}
        columns={columns}
        loading={isLoading}
        rowKey={(a) => String(a.id)}
        total={filteredAliases.length}
        page={safePage}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyText={query ? '没有匹配的别名' : '该域名下暂无别名，点击左上角「新建别名」'}
        toolbarLeft={
          <>
            <Button appearance="primary" size="small" icon={<AddRegular />} onClick={openCreate}>
              新建别名
            </Button>
            <Switch
              checked={selectionMode === 'multiselect'}
              onChange={(_, d) => setSelectionMode(d.checked ? 'multiselect' : 'single')}
              label="多选"
            />
          </>
        }
        selectionMode={selectionMode}
        onSelectionChange={setSelectedRows}
        onDelete={(rows) => deleteMutation.mutateAsync(rows)}
        onRowClick={(a) => setDetailAlias(a)}
      />

      {/* 新建别名 */}
      <Dialog open={createOpen} onOpenChange={(_, d) => { if (!d.open) setCreateOpen(false) }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="新建别名" onClose={() => setCreateOpen(false)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field className={styles.formGridItem} label={<InfoLabel info="别名邮箱的用户名部分，与所选域名组成完整地址。">地址</InfoLabel>} required hint="如 info">
                  <Input value={form.address} onChange={(_, d) => setForm((f) => ({ ...f, address: d.value }))} placeholder="info" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="别名所属的邮件域，取自左侧选中的收件域。">域名</InfoLabel>} required>
                  <Input value={domain.name} disabled />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="别名转发到的目标邮箱地址。">目标</InfoLabel>} required hint="如 user1@example.com">
                  <Input value={form.target} onChange={(_, d) => setForm((f) => ({ ...f, target: d.value }))} placeholder="user1@example.com" />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={handleSubmit} disabled={createMutation.isPending}>
                {createMutation.isPending ? '创建中…' : '创建'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 详情 */}
      <Dialog open={!!detailAlias} onOpenChange={(_, d) => !d.open && setDetailAlias(null)} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="别名详情" onClose={() => setDetailAlias(null)} />
            <DialogContent>
              {detailAlias && (
                <DetailGrid key={detailAlias.id}>
                  <DetailField label="地址" value={`${detailAlias.address}@${detailAlias.domain}`} mono />
                  <DetailField label="目标地址" value={detailAlias.target} mono />
                </DetailGrid>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}